export enum SupportKioskColumn {
    Id = 0,
    CifAccount,
    Name,
    KioskType,
    MachineID,
    SerialNumber,
    DMS,
    Inactive,
    GoMotoApp,
    GoMotoAppUpdatedOn,
    ConfigApp,
    ConfigAppUpdatedOn,
    UpdaterApp,
    UpdaterAppUpdatedOn,
    Offline,
    LastConnected,
    OnMaintenance,
}

export enum SortOrder {
    asc = 0,
    desc,
}

/**
 * Dealer Management System (DMS) type identifiers. Values correspond to the database DmsType column.
 */
export enum DmsType {
    /**
     * No DMS configured
     */
    None = 0,
    /**
     * DealerTrack
     */
    DT = 3,
    /**
     * Autosoft
     */
    AS = 4,
    /**
     * Automate (Xtime)
     */
    AM = 5,
    /**
     * Dealerbuilt (LightYear)
     */
    DB = 6,
    /**
     * Reynolds and Reynolds
     */
    RR = 7,
    /**
     * PBS Systems
     */
    PB = 9,
    /**
     * Quorum Information Technologies
     */
    QI = 10,
    /**
     * CDK Fortellis
     */
    CDF = 11,
    /**
     * Serti
     */
    SI = 12,
}

export enum HealthCheckColumn {
    Timestamp = 0,
    Name,
    MachineID,
    StatusCode,
    CPUTemp,
    InternalTemp,
    SignalStrength,
    Pi,
}

/**
 * Column indexes for the Notification Delivery Status report table.
 */
export enum NotificationDeliveryStatusReportColumn {
    /**
     * Dealer name column.
     */
    DealerName = 0,
    /**
     * Total texts sent column.
     */
    TextsSent,
    /**
     * Delivered texts column.
     */
    TextsDelivered,
    /**
     * Undelivered texts column.
     */
    TextsUndelivered,
    /**
     * Failed texts column.
     */
    TextsFailed,
    /**
     * Total emails sent column.
     */
    EmailsSent,
    /**
     * Delivered emails column.
     */
    EmailsDelivered,
    /**
     * Emails that were not delivered column.
     */
    EmailsNotDelivered,
    /**
     * Opened emails column.
     */
    EmailsOpened,
    /**
     * Clicked emails column.
     */
    EmailsClicked,
}

/**
 * Display mode for the Notification Delivery Status report metrics.
 */
export enum NotificationDeliveryStatusViewAs {
    /**
     * Show metrics as raw counts.
     */
    Counts = 0,
    /**
     * Show metrics as percentages of totals.
     */
    Percentages,
}

export enum SmsWhitelistColumn {
    Phone = 0,
    Name,
    Username,
    Created,
    Modified
}

export enum HunterVehicleColumn {
    Id = 0,
    Timestamp,
    DealerName,
    VIN,
    Year,
    Make,
    Model,
    WheelAlignment,
    TireCheck
}

export enum Country {
    US = 0,
    Canada = 1,
}

export enum CRMProvider {
    None = 0,
    Reynolds = 1,
    Affinitiv = 2,
    DealerSocket = 3,
    eLeads = 4,
    GmGlobalConnect = 5,
    VinSolutions = 6,
    Other = 7,
}

/**
 * Valuation screen types displayed to customers during kiosk check-in.
 * Controls which trade-in valuation experience the customer sees.
 */
export enum ValuationScreen {
    /**
     * No valuation screen shown
     */
    None = 0,
    /**
     * Standard Blackbook trade-in valuation
     */
    Blackbook = 1,
    /**
     * Equity-detected valuation prompt
     */
    EquityDetected = 2,
    /**
     * Custom value splash screen for BMW Honolulu
     */
    ValueSplash_BMWHONOLULU = 3,
    /**
     * Custom value splash screen for MINI Hawaii
     */
    ValueSplash_MINIHAWAII = 4,
}

/**
 * Mobile-specific valuation screen types.
 * Subset of ValuationScreen options available on mobile check-in.
 */
export enum MobileValuationScreen {
    /**
     * Standard Blackbook trade-in valuation
     */
    Blackbook = 1,
    /**
     * Equity-detected valuation prompt
     */
    EquityDetected = 2,
}

/**
 * Method used to adjust Blackbook trade-in valuations.
 * Applied as either a percentage multiplier or a flat dollar amount.
 */
export enum BlackbookAdjustmentMethod {
    /**
     * Adjust by a percentage of the Blackbook value
     */
    Percentage = 1,
    /**
     * Adjust by a fixed dollar amount
     */
    FlatRate = 2,
}

/**
 * Custom question types available at the end of a kiosk check-in.
 * Determines whether the question is a freeform comment or a service-related prompt.
 */
export enum CustomQuestionType {
    /**
     * No custom question
     */
    None = 0,
    /**
     * Freeform comment question
     */
    Comment = 1,
    /**
     * Service-related question
     */
    Service = 2,
}

/**
 * Welcome message options played to customers at the start of a kiosk check-in.
 */
export enum WelcomeMessage {
    /**
     * Static "Welcome" greeting
     */
    Welcome = 0,
    /**
     * Time-of-day greeting (Good Morning/Afternoon/Evening) based on dealer timezone
     */
    GoodMorningAfternoonEvening = 1,
}

/**
 * Thank-you message options displayed to customers at the end of a kiosk check-in.
 */
export enum ThankYouMessage {
    ThankYouHaveAGreatDay = 0,
    ThankYouLookForwardToFeedback = 1,
    ThankYouAppreciateYou = 2,
}

export enum DayOfWeek {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
}

export enum SupportUserColumn {
    Id = 0,
    FirstName,
    LastName,
    Username,
    Email,
    Phone,
    Role
}

export enum DashboardUserColumn {
    Id = 0,
    FirstName,
    LastName,
    Username,
    Email,
    Phone,
    Notifications,
    Inactive,
    Locked,
    EditIcon
}


export enum PermissionLevel {
    None = 0,
    YesFull,
    View,
}

export enum UserPermissions {
    // Dashboard
    ActivityDashboardAccess = 1,
    ActivityDashboard_ViewReceiptTab = 2,
    ActivityDashboard_AuditTrailTab = 3,
    SummaryDashboardAccess = 4,
    SummaryDashboard_CompareView = 5,
    SummaryDashboard_BrandView = 6,
    UserManagementAccess = 7,
    SummaryDashboard_ViewOtherAdvisors = 8,
    SummaryDashboard_SetChangeGoals = 9,
    UserManagement_AddUsers = 10,
    UserManagement_DeleteUsers = 11,
    UserManagement_GeneratePasswords = 12,
    KeyManagement_Mobile = 13,
    QRCodeScan_Mobile = 14,
    ActivityDashboard_ROAddEditAfterPrintedReceipt = 15,
    Settings_GeneralSettings = 16,
    Settings_DisclaimersAndConsents = 17,
    Settings_Communications = 18,

    // Support Hub
    SupportKiosk = 1000,
    SupportHealthCheck = 1001,
    SupportUserManagement = 1002,
    SupportAddKiosk = 1003,
    SupportDeleteKiosk = 1004,
    SupportUserManagementAddUser = 1005,
    SupportUserManagementDeleteUser = 1006,
    SupportDashboard = 1007,
    SupportResponseLog = 1008,
    SupportKioskBanner = 1009,
    SupportAddKioskBanner = 1010,
    SupportDeleteKioskBanner = 1011,
    SupportDealerGroup = 1012,
    SupportAddDealerGroup = 1013,
    SupportDeleteDealerGroup = 1014,
    SupportDealerSettings = 1015,
    SupportDeleteDealerSettings = 1016,
    SupportFortellisSwapTables = 1017,
    SupportReports = 1018,
}

export enum LandingPage {
    // Dashboard
    Summary = 1,
    Activity = 2,

    // Support Hub
    Kiosk = 1000,
    HealthCheck = 1001,
    ResponseLog = 1002,
    UserManagement = 1003,
    DealerSettings = 1004,
    DealerGroups = 1005,
    KioskBanners = 1006,
    Reports = 1007,
    Services = 1008,
}

export enum BottomLeftGoalBoxSetting {
    Satisfaction = 0,
    Pickup = 1,
}

export enum LoginLocation {
    Dashboard = 1,
    Kiosk,
    Support,
}

export enum UserChangeActionTypes {
    UserAdded = 1,
    UserLocked = 2,
    UserUnlocked = 3,
    UserActivated = 4,
    UserInactivated = 5,
    PermissionsUpdated = 6,
    NotificationChoicesUpdated = 7,
    PinUpdated = 8,
    AdvisorNumberUpdated = 9,
    EmailAddressUpdated = 10,
    NameUpdated = 11,
    PhoneUpdated = 12,
    PasswordGenerated = 13,
    DSIMIdUpdated = 14,
    RoleUpdated = 15,
    LanguageUpdated = 16,
    TextOptInUpdated = 17,
    TextOptIn = 19,
    TextOptOut = 20,
    TextError = 21,
    DealerInactiveNotificationsDisabled = 22,
    TextOptOutInstructionsSent = 23,
    ROReminderEmailChoicesUpdated = 24,
    ROReminderTextChoicesUpdated = 25
}

export enum ExportFileType {
    Text = 0,
    CSV,
    Excel,
}

export enum Timezone {
    Default = 0,
    Alaska,
    Arizona,
    Central,
    Eastern,
    Hawaii,
    Mountain,
    Pacific,
    Atlantic,
    Newfoundland
}

export enum ResponseLogColumn {
    Id,
    Timestamp,
    Provider,
    DealerId,
    DealerName,
    TransactionName,
    VIN,
    StatusCode,
    ResultCode,
    ResultMessage,
}

export enum Provider {
    Automate = 0,
    Autosoft = 1,
    CDKFortellis = 4,
    Dealerbuilt = 5,
    Dealertrack = 6,
    KiaRecall = 7,
    MasterRecall = 8,
    PBS = 9,
    Quorum = 10,
    Reynolds = 11,
    VWRecall = 12,
    Xtime = 13,
    Blackbook = 14,
    CDP = 15,
    Twilio = 16,
    KeyTrak = 17
}

export enum BannerImageColumn {
    Id,
    Dealers,
    Kiosk,
    ImageUrl,
    ImagePreview
}

export enum DealerGroupColumn {
    Id,
    Name,
    Dealers
}

export enum DealerStatus {
    Inactive = 0,
    Active = 1,
    PreInstall = 2,
    Test = 3
}

export enum SupportDealerColumn {
    Id = 0,
    Name,
    CifAccount,
    DMS,
    DMSId,
    Country,
    TimeZone,
    Status,
    Hunter,
    Recalls,
    Xtime
}

export enum VehicleDataProvider {
    ChromeUS = 1,
    ChromeCanada = 2
}

export enum RecallType {
    None = 'none',
    Master = 'master recall',
    VW = 'vw recall',
    KIA = 'kia recall'
}

export enum FortellisSwapTableColumn {
    DealerNames,
    CDKSubscriptionId,
    OpCode,
    Name,
    LaborType,
    DispatchCode
}

/**
 * Enumeration for each column that is displayed on the Services screen's table.
 */
export enum ServicesColumn {
    /**
     * The name of the service.
     */
    Name,
    /**
     * The dealer that is directly associated with the service. Displays "No Dealer Assigned" if one is not associated yet.
     */
    Dealer,
    /**
     * A list of all the kiosks the service belongs to.
     */
    Kiosk,
    /**
     * The `item_reference_id` column for the service.
     */
    Opcode,
    /**
     * The Category of the service.
     */
    ServiceType,
    /**
     * The `mileage_min_filter` column for the service.
     */
    MileageMin,
    /**
     * The `mileage_max_filter` column for the service.
     */
    MileageMax,
    /**
     * Displays a checkmark when `use_spg_pricing` is true, otherwise does not display anything.
     */
    SpgPriced,
    /**
     * The formatted price in USD for the service.
     */
    Price,
    /**
     * The `order_filter` column for the service, displayed as "Priority". Only Upsell Service and
     * Upsell Accessory service types are expected to have a value.
     */
    Priority,
    /**
     * The image associated with the service. Displays custom over generic if a custom image exists.
     */
    Image,
    /**
     * Whether the service is offered in the service cart. Displays "Common - In Cart" when the service
     * is also flagged as a common service, "In Cart" when it is not, and a dash when the service is not
     * offered in the cart.
     */
    ServiceCart,
}

export enum HistoryActionType {
    Added = 1,
    Changed,
    Deleted,
    SoftwareUpdated,
    // Displays the field text as is without pre-pending added, changed, deleted, or updated
    Action
}

export enum PaymentProvider {
    None = 0,
    StripeCanada,
    WorldPayUS
}

export enum TransportationOptionType {
    DropOff,
    Shuttle,
    Waiter,
    Loaner,
    NotNeeded
}

export enum TransportationOptionSubtype {
    None,
    Shuttle,
    Lyft,
    Uber,
    Enterprise
}

/**
 * DMS transportation type options for the Custom transportation option (CDK only).
 */
export enum CustomDmsTransportationType {
    None = '',
    Cab = 'Cab',
    Shuttle = 'Shuttle',
    Other = 'Other',
}

export enum AppointmentLookupMethod {
    Unknown = 'unknown',
    Name = 'name',
    PhoneNumber = 'phonenumber',
    Business = 'business'
}

export enum AppointmentType {
    Service,
    BodyShop,
    QuickLube,
}

export enum AppointmentNotificationName {
    AppointmentReminder = 'AppointmentReminder',
    CheckInConfirmation = 'CheckInConfirmation',
    OnlineCheckInConfirmed = 'OnlineCheckInConfirmed',
    OnlineCheckInSaved = 'OnlineCheckInSaved',
    PaymentDue = 'PaymentDue',
    ReadyForPickup = 'ReadyForPickup',
    PickupReceipt = 'PickupReceipt',
    PhoneUpdated = 'PhoneUpdated',
    MFAEmailUpdate = 'MFAEmailUpdate',
}

export enum QRCodeType {
    unknown = 0,
    mobileSaveSession,
    mobileComplete,
    vehicleReady
}

export enum SpgPricingType {
    Range = 1,
    Minimum,
    Maximum,
    NoPrice
}

export enum MaintenanceMenuType {
    Schedule = 1,
    Mileage
}

export enum RoReceipt {
    Optional = 1,
    Required
}

export enum AuditTrailEvent {
    CheckInComplete = 0,
    CheckInAbandoned,
    DetailsViewed,
    SignatureViewed,
    ReceiptViewed,
    ReceiptPrinted,
    KeyLoaded,
    CheckInStarted,
    PickedUp,
    MobileCheckInatKioskStarted,
    LockerOpened,
    LockerClosed,
    ManualReturn,
    CustomerKeyLoaded,
    CustomerPickedUp,
    ReopenedLocker,
    RONumberAdded,
    RONumberUpdated,
    AgreedToElectronicSignatures,
    CheckInAlertComplete,
    CheckInAlertUnread,
    DMSRONumberDoesNotMatch,
    PaymentInProcess,
    PaymentReceived,
    PaymentSessionExpired,
    PickupDetailsViewed,
    PaymentUnsuccessfulReversed,
    TextUndeliverable,
    TextOptIn,
    TextOptOut,
    TextError,
    MarkedPaid,
    PickupNotificationEmailResent,
    PickupNotificationTextResent,
    PickupNotificationTextAndEmailResent,
    AppointmentReminderEmailSent,
    EmailUpdatedTextSent,
    PhoneNumberUpdatedEmailSent,
    OnlineCheckInConfirmedEmailSent,
    OnlineCheckInStatusSavedEmailSent,
    CheckInConfirmedEmailSent,
    CheckInConfirmedTextSent,
    PaymentThankYouGetKeysEmailSent,
    PaymentThankYouGetKeysTextSent,
    PaymentThankYouGetKeysEmailResent,
    PaymentThankYouGetKeysTextResent,
    PaymentDueEmailSent,
    PaymentDueTextSent,
    PaymentDueEmailResent,
    PaymentDueTextResent,
    PickupReadyEmailSent,
    PickupReadyTextSent,
    PickupReadyEmailResent,
    PickupReadyTextResent,
    ReceiptEmailSent,
    ReceiptTextSent,
    ReceiptEmailResent,
    ReceiptTextResent,
    AppointmentReminderTextSent,
    OnlineCheckInConfirmedTextSent,
    OnlineCheckInStatusSavedTextSent,
    LoanerKeyReturnedToLocker = 61,
    RentalKeyReturnedToLocker = 62,
    LoanerKeyRetrievedFromLocker = 63,
    RentalKeyRetrievedFromLocker = 64,
    CustomerKeyPickedUp = 65,
    PaymentAmountUpdated = 66,
    OptOutInstructionsSent = 67,
    CheckInSaved = 68,
    PrecheckInComplete = 69,
    ROReminderTextSent = 70,
    ROReminderEmailSent = 71,
}

export enum LoginErrors {
    None = 0,
    FailedToRetrievePaymentAccountStatusError = 'FailedToRetrievePaymentAccountStatusError',
    AccountNoAccessError = 'AccountNoAccessError',
    AuthorizationError = 'AuthorizationError',
    AccountLockedError = 'AccountLockedError',
    AccountInactiveError = 'AccountInactiveError',
    UserNotFoundError = 'UserNotFoundError',
    AccountCredentialsError = 'AccountCredentialsError',
    PasswordValidationError = 'PasswordValidationError',
    EmailValidationError = 'EmailValidationError',
    SessionExpiredError = 'SessionExpiredError',
    Other = -1
}

export enum DashboardUserErrors {
    PinInUseError = 'PinInUseError',
    UserExistsError = 'UserExistsError',
    PasswordDoesNotMatchError = 'PasswordDoesNotMatchError',
    NewPasswordDoesNotMeetRequirementsError = 'NewPasswordDoesNotMeetRequirementsError',
    LoggedInUserLockedError = 'LoggedInUserLockedError',
    LoggedInUserInactiveError = 'LoggedInUserInactiveError',
    FailedToEnrollInDSIMError = 'FailedToEnrollInDSIMError',
    EmailNotUniqueError = 'EmailNotUniqueError',
    CantDeleteLoggedInUserError = 'CantDeleteLoggedInUserError'
}

export enum SignalrEnrollment {
    LockerStatus = 'enrollForLockerStatus',
    ManuallyComplete = 'enrollForManuallyCompleteAppointment',
    AppointmentKeyPickedUp = 'enrollForAppointmentKeyPickedUp',
    AppointmentKeyStored = 'enrollForAppointmentKeyStored'
}

export enum SignalrEvent {
    LockerStatus = 'LockerStatus',
    ManuallyComplete = 'ManuallyComplete',
    AppointmentKeyStored = 'AppointmentKeyStored',
    AppointmentKeyPickedUp = 'AppointmentKeyPickedUp'
}

export enum BusinessType {
    None = 0,
    AssociationEstateTrust,
    FINInstitute,
    Government,
    LLC,
    LLP,
    NonProfit,
    Partnership,
    PrivateCorporation,
    ProfessionalCorporation,
    PublicCorporation,
    SCorporation,
    SECRegistered,
    SoleProprietor,
    Other
}

export enum SendNotificationResult {
    EmailSent = 1,
    TextSent,
    EmailAndTextSent,

    // Errors
    AppointmentNotFound,
    UnknownQRCode,
    FailedToSend,
    NotOptedInForTexts,
    OptedOutForTexts
}

export enum TextOptInStatus {
    OptedOut = 0,
    OptedIn,
    NotDecided
}

export enum ErrorLevel {
    None = 0,
    Warning,
    Error
}

/**
 * Reynolds repair order department when Use RO Create is enabled
 */
export enum RODepartment {
    /**
     * No override for Reynolds RO department
     */
    None = '',
    /**
     * Indicate Reynolds RO to Service Department
     */
    Service = 'S',
    /**
     * Indicate Reynolds RO to Body Shop Department
     */
    BodyShop = 'B',
    /**
     * Indicate Reynolds RO to PDI Department
     */
    PDI = 'P'
}

/**
 * Reynolds walk-in department for ERA dealers
 */
export enum WalkInDepartment {
    /**
     * Indicate ERA walk-in deparmtent as Service Department
     */
    Service = 'S',
    /**
     * Indicate ERA walk-in deparmtent as One Step Department
     */
    OneStep = 'Q',
    /**
     * Indicate ERA walk-in deparmtent as Body Shop Department
     */
    BodyShop = 'B',
    /**
     * Indicate ERA walk-in deparmtent as PDI Department
     */
    PDI = 'P'
}

/**
 * Indicates what kind of vehicles a dealership will temporarily give out to customers
 * that will later be returned by the customer to the Kiosk.
 */
export enum LoanerOrRentalOption {
    /**
     * The dealership is opted out of Rentals/Loaners.
     */
    None = 'None',
    /**
     * The dealership only offers Loaners and only uses Loaner functionality.
     */
    Loaners = 'Loaners',
    /**
     * The dealership only offers Rentals and only uses Rental functionality.
     */
    Rentals = 'Rentals',
    /**
     * The dealership offers both Rentals and Loaners and uses the functionality from both.
     */
    Both = 'Both'
}

/**
 * The options available for an appointment regarding Loaner/Rental options. These
 * options are reduced depending on the dealer's setting.
 */
export enum AppointmentLoanerOrRentalOption {
    /**
     * The customer will not have a Loaner or Rental to return.
     */
    None = 'None',
    /**
     * The customer will return a Loaner.
     */
    Loaner = 'Loaner',
    /**
     * The customer will return a Rental.
     */
    Rental = 'Rental'
}

/**
 * Indicates whether a dealership will allow walk-ins and whether they will be limited to
 * existing customers only.
 */
export enum WalkInsOption {
    /**
     * Walk-ins are not allowed.
     */
    None = 0,
    /**
     * Walk-ins are allowed, but only for existing customers.
     */
    ExistingCustomersOnly,
    /**
     * Walk-ins are allowed for new and existing customers.
     */
    All
}

/**
 * Indicates which mobile check-in path a mobile-only dealer uses:
 * One-Step (Im-Here) or Two-Step (the default path).
 */
export enum MobilePathOption {
    /**
     * One-Step: the mobile site redirects to the Im-Here path.
     */
    OneStep = 0,
    /**
     * Two-Step: the mobile site uses the current default path.
     */
    TwoStep
}

/**
 * Controls whether an Express kiosk offers locker key dropoff at the end of a
 * check-in, and whether the tow-in feature is offered. Only displayed for, and
 * only read by, the Express kiosk type.
 */
export enum KeyDropoffOption {
    /**
     * On: locker key dropoff is offered at the end of every check-in.
     */
    On = 0,
    /**
     * Off: locker key dropoff is never offered; the consumer is directed to a service advisor.
     */
    Off,
    /**
     * After Hours Only: locker key dropoff is offered only outside the kiosk's Kiosk Hours.
     */
    AfterHours
}

/**
 * Indicates whether the VIN field displays during a check-in and whether
 * it is required.
 */
export enum VinOption {
    /**
     * No VIN field displayed during check-in.
     */
    None = 0,
    /**
     * VIN field displayed during check-in, but providing it is optional.
     */
    Optional,
    /**
     * VIN field displayed during check-in, and it is required.
     */
    Required
}

/**
 * Indicates whether the Tag Number field displays during a check-in and whether
 * it is required.
 */
export enum TagNumberOption {
    /**
     * No Tag Number field displayed during check-in.
     */
    None = 0,
    /**
     * Tag Number field displayed during check-in, but providing it is optional.
     */
    Optional,
    /**
     * Tag Number field displayed during check-in, and it is required.
     */
    Required
}

/**
 * Enumeration of meridiem notations.
 */
export enum Meridiem {
    /**
     * AM (Anti-Meridiem) time notation.
     */
    AM = 'AM',
    /**
     * PM (Post-Meridiem) time notation.
     */
    PM = 'PM'
}

/*
 * Enumeration of the kiosk status filter control in the Kiosk Configuration
 * support screen.
 */
export enum KioskStatus {
    /**
     * Only show kiosks with the active status.
     */
    Active = 'Active',
    /**
     * Only show kiosks with the inactive status.
     */
    Inactive = 'Inactive',
    /**
     * Show all kiosks regardless of status.
     */
    Both = 'Both'
}

/**
 * An enum of the possible test results for Wheel Alignments or Tire Checks
 */
export enum HunterTestResult {
    /**
     * Only show vehicles that passed test
     */
    Passed = 'Passed',
    /**
     * Only show vehicles that failed test
     */
    Failed = 'Failed'
}

/**
 * An enum of all the possible results for any measurement in the Wheel Alignement or Tire Check sections.
 */
export enum HunterResult {
    /**
     * No result for an alignment or tire check measurement.
     */
    NoResult = 0,
    /**
     * A good result for an alignment or tire check measurement.
     */
    Good,
    /**
     * A warning that the wheel alignment or tire check measurement is a problem.
     */
    Warning,
    /**
     * The alignment or tire check measurement is severe and should be addressed.
     */
    Severe,
    /**
     * The alignment or tire check measurement is dangerous.
     */
    Danger,
    /**
     * The test was aborted for an alignment or tire check measurement.
     */
    TestAborted,
    /**
     * There was an error on the test for an alignment or tire check measurement.
     */
    TestError,
    /**
     * There was a timeout on the test for an alignment or tire check measurement.
     */
    TestTimeout
}

export enum HunterAuditResult {
    Passed = 0,
    Recommended
}

/**
 * Enumeration for the SPG Pricing select on the Edit Service screen.
 */
export enum SpgPricing {
    Range = 1,
    Minimum = 2,
    Maximum = 3,
    NoPrice = 4,
}

/**
 * Enumeration for the Category select on the Edit Service screen.
 */
export enum ServiceCategory {
    All = 0,
    AirConditioner = 1,
    AirFiltration = 2,
    Alignment = 3,
    Battery = 4,
    Brakes = 5,
    CoolingSystem = 6,
    Drivetrain = 7,
    Inspection = 8,
    Emissions = 9,
    Engine = 10,
    FuelSystem = 11,
    Lights = 12,
    Wipers = 13,
    Oil = 14,
    Recall = 15,
    Suspension = 16,
    Tires = 17,
    Transmission = 18,
    Other = 19,
    _5000K = 20,
    _7500K = 21,
    _10000K = 22,
    _15000K = 23,
    _20000K = 24,
    _30000K = 25,
    _40000K = 26,
    _45000K = 27,
    _60000K = 28,
    _75000K = 29,
    _80000K = 30,
    _90000K = 31,
    _100000K = 32,
    _120000K = 33,
    _160000K = 34,
    ScheduledService = 35,
    Maintenance = 36,
    PDI = 37,
    UCINotCertified = 38,
    UCICertified = 39,
}

/**
 * Enumeration for the Generic Service select on the Edit Service screen in the Generic Service tab.
 */
export enum GenericService {
    Default = 0,
    WiperBlades = 1,
    OilChange = 2,
    AirConditionerCheck = 3,
    CoolantFlush = 4,
    BrakeFlush = 5,
    FuelFlush = 6,
    TransmissionFluidFlush = 7,
    FrontEndAlignment = 8,
    FourWheelAlignment = 9,
    TireRotationAndBalance = 10,
    TireSpecial = 11,
    BatteryTerminalCleaning = 12,
    BatteryReplacement = 13,
    CabinAirFilterReplacement = 14,
    EngineAirFilterReplacement = 15,
    FullDetail = 16,
    ExteriorDetail = 17,
    InteriorDetail = 18,
    OilChangeConventional = 19,
    OilChangeSynthetic = 20,
    EngineOilAdditive = 21,
    WashWaxVaccum = 22,
    RoadForceBalanceTires = 23,
    HeadlampRestoration = 24,
    WinterSummerTireSwap = 25,
    AcOdorTreatment = 26,
    TireRotation = 27,
    CabinEngineAirFilterReplacement = 28,
    WiperInserts = 29,
    GenuineToyotaConventionalOil = 30,
    GenuineToyotaFullSyntheticOil = 31,
    MobilOneAdvanceFullSynthetic = 32
}

/**
 * An enum-like object representing the type of a service offered by a dealer.
 * Used to categorize services on the Support Services screens.
 */
export const ServiceType = {
    /**
     * General – A standard service offered by the dealer.
     */
    General: 0,
    /**
     * Upsell Service – A service presented to the customer as an upsell during check-in.
     */
    UpsellService: 1,
    /**
     * Upsell Accessory – An accessory presented to the customer as an upsell during check-in.
     */
    UpsellAccessory: 2,
    /**
     * Miscellaneous – Any service that does not fit into the other categories.
     */
    Miscellaneous: 3,
} as const;

/**
 * A union type of all the values used in the {@link ServiceType} type to make it similarly as easy as typing something
 * with an enum.
 */
export type ServiceTypeValues = typeof ServiceType[keyof typeof ServiceType];

/**
 * An enum-like object representing the tri-state choice on the Support Services screen's "Unassigned Services"
 * filter. Determines whether the table includes all services, only services with no kiosks linked, or only
 * services with at least one kiosk linked.
 */
export const UnassignedServicesFilter = {
    /**
     * All – Show every service regardless of kiosk assignment. Default value.
     */
    All: 0,
    /**
     * Unassigned – Show only services that have no kiosks linked via `KeydropAdmin_service_kiosks`.
     */
    Unassigned: 1,
    /**
     * Assigned – Show only services that have at least one kiosk linked via `KeydropAdmin_service_kiosks`.
     */
    Assigned: 2,
} as const;

/**
 * A union type of all the values used in the {@link UnassignedServicesFilter} type to make it similarly as easy
 * as typing something with an enum.
 */
export type UnassignedServicesFilterValues = typeof UnassignedServicesFilter[keyof typeof UnassignedServicesFilter];

/**
 * Maps numeric timezone identifiers (as returned by the API) to IANA timezone strings
 * suitable for use with {@link Intl.DateTimeFormat} or date libraries like `date-fns-tz`.
 */
export const TimezoneConversion = {
    0: 'America/New_York',
    1: 'America/Anchorage',
    2: 'America/Phoenix',
    3: 'America/Chicago',
    4: 'America/New_York',
    5: 'Pacific/Honolulu',
    6: 'America/Denver',
    7: 'America/Los_Angeles',
    8: 'America/Puerto_Rico',
    9: 'America/St_Johns',
} as const;

/**
 * An enum-like object controlling whether a KPI report's rows are grouped per dealer or per kiosk.
 */
export const KpiReportViewType = {
    /**
     * Dealer – One row per dealership; kiosk name omitted.
     */
    Dealer: 0,
    /**
     * Kiosk – One row per kiosk; kiosk name included.
     */
    Kiosk: 1,
} as const;

/**
 * A union type of all the values used in the {@link KpiReportViewType} type to make it similarly as easy as typing something
 * with an enum.
 */
export type KpiReportViewTypeValues = typeof KpiReportViewType[keyof typeof KpiReportViewType];

/**
 * An enum-like object representing the metric shown for the count-based columns across all three KPI reports.
 * Each report only accepts a subset of these values.
 */
export const KpiReportMetricType = {
    /**
     * Counts – Display the raw count for each column.
     */
    Counts: 0,
    /**
     * Percent Change – Display the percentage change versus the prior comparable period.
     */
    PercentChange: 1,
    /**
     * Percent of DMS Appointments – Check-In Completion report only.
     */
    PercentDmsAppointments: 2,
    /**
     * Rates – Check-In Selections report only.
     */
    Rates: 3,
} as const;

/**
 * A union type of all the values used in the {@link KpiReportMetricType} type to make it similarly as easy as typing something
 * with an enum.
 */
export type KpiReportMetricTypeValues = typeof KpiReportMetricType[keyof typeof KpiReportMetricType];

/**
 * An enum-like object identifying the sortable columns on the Check-In Completion KPI report.
 */
export const KpiCheckInCompletionColumn = {
    Dealer: 0,
    Cif: 1,
    Region: 2,
    Dms: 3,
    Kiosk: 4,
    KioskType: 5,
    DmsAppointments: 6,
    MobileIncomplete: 7,
    PrecheckComplete: 8,
    MobileComplete: 9,
    KioskIncomplete: 10,
    KioskComplete: 11,
    MobileToKioskComplete: 12,
    TotalCheckIns: 13,
    AfterHoursDrops: 14,
    TowIns: 15,
    EasierPercent: 16,
    AnsweredPercent: 17,
    AverageCheckInTime: 18,
} as const;

/**
 * A union type of all the values used in the {@link KpiCheckInCompletionColumn} type to make it similarly as easy as typing
 * something with an enum.
 */
export type KpiCheckInCompletionColumnValues = typeof KpiCheckInCompletionColumn[keyof typeof KpiCheckInCompletionColumn];

/**
 * An enum-like object identifying the sortable columns on the Check-In Selections KPI report.
 */
export const KpiCheckInSelectionsColumn = {
    Dealer: 0,
    Cif: 1,
    Region: 2,
    Manufacturer: 3,
    Dms: 4,
    Kiosk: 5,
    KioskType: 6,
    TotalCheckIns: 7,
    Upsells: 8,
    UpsellRevenue: 9,
    TradeEvals: 10,
    Recalls: 11,
    AlignmentsRecommended: 12,
    AlignmentsSold: 13,
    RndSold: 14,
    RndDoneElsewhere: 15,
    ContactUpdates: 16,
} as const;

/**
 * A union type of all the values used in the {@link KpiCheckInSelectionsColumn} type to make it similarly as easy as typing
 * something with an enum.
 */
export type KpiCheckInSelectionsColumnValues = typeof KpiCheckInSelectionsColumn[keyof typeof KpiCheckInSelectionsColumn];

/**
 * An enum-like object identifying the sortable columns on the Pickup KPI report.
 */
export const KpiPickupColumn = {
    Dealer: 0,
    Cif: 1,
    Region: 2,
    Manufacturer: 3,
    Dms: 4,
    Kiosk: 5,
    KioskType: 6,
    TotalPickups: 7,
    AfterHoursPickups: 8,
    LoanerReturns: 9,
    RentalReturns: 10,
    OnlinePayRevenue: 11,
} as const;

/**
 * A union type of all the values used in the {@link KpiPickupColumn} type to make it similarly as easy as typing something
 * with an enum.
 */
export type KpiPickupColumnValues = typeof KpiPickupColumn[keyof typeof KpiPickupColumn];

/**
 * An enum-like object representing the geographic region a dealer is grouped into for reporting. Member names match the
 * codes used by {@link useGetUserRegionOptions} (`src/Contexts/User.tsx`) with the underscores removed.
 */
export const Region = {
    USAMidwest: 0,
    USANortheast: 1,
    USASoutheast: 2,
    USAWest: 3,
    CanadaCentral: 4,
    CanadaEast: 5,
    CanadaOntario: 6,
    CanadaQuebec: 7,
    CanadaWest: 8,
} as const;

/**
 * A union type of all the values used in the {@link Region} type to make it similarly as easy as typing something
 * with an enum.
 */
export type RegionValues = typeof Region[keyof typeof Region];
